import { useState } from "react";
import { Loader2, ArrowRight, Activity, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { THERAPY_OPTIONS } from "../../lib/therapyTypes";
import { toast } from "sonner";
import { updateProfile } from "../../lib/api";

export default function OnboardingModal({ open, onComplete, defaultValue }) {
  const [selected, setSelected] = useState(defaultValue || "physiotherapy");
  const [busy, setBusy] = useState(false);

  const handleSave = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const user = await updateProfile({ therapy_type: selected });
      toast.success("All set", {
        description: `Your fields are tuned for ${user.therapy_type
          .replace(/^./, (c) => c.toUpperCase())}.`,
      });
      onComplete(user);
    } catch (e) {
      toast.error("Couldn't save", { description: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        data-testid="onboarding-modal"
        className="max-w-2xl rounded-3xl p-0 overflow-hidden border-slate-200/80 bg-white"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Welcome to TherapyNote — pick your therapy type</DialogTitle>
        <DialogDescription className="sr-only">
          Choose your clinical discipline so field labels and AI extraction
          adapt accordingly.
        </DialogDescription>
        <div className="px-7 pt-7 pb-3 bg-gradient-to-br from-[#ECFDF5] to-white border-b border-slate-100">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-[#10B981] text-white shadow-[0_4px_12px_rgba(16,185,129,0.3)]">
              <Activity className="w-5 h-5" strokeWidth={2.5} />
            </span>
            <div>
              <div className="font-figtree text-xs uppercase tracking-[0.18em] text-[#065F46] font-semibold">
                Welcome to TherapyNote
              </div>
              <h2 className="font-outfit text-2xl font-bold tracking-tight text-[#0F172A]">
                Which therapy do you practice?
              </h2>
            </div>
          </div>
          <p className="font-figtree text-sm text-[#475569] mt-3 max-w-xl">
            We'll relabel your evaluation and daily-note fields, and tune the AI
            extraction so it routes speech into the right clinical sections for
            your discipline. You can change this any time.
          </p>
        </div>

        <div className="px-7 py-6 max-h-[60vh] overflow-y-auto">
          <div className="grid sm:grid-cols-2 gap-2.5">
            {THERAPY_OPTIONS.map((opt) => {
              const active = selected === opt.value;
              return (
                <button
                  key={opt.value}
                  data-testid={`onboarding-option-${opt.value}`}
                  onClick={() => setSelected(opt.value)}
                  aria-pressed={active}
                  className={`relative text-left rounded-2xl border p-4 transition-all ${
                    active
                      ? "border-[#10B981] bg-[#ECFDF5] shadow-[0_6px_18px_rgba(16,185,129,0.18)]"
                      : "border-slate-200 hover:border-[#A7F3D0] hover:bg-[#F8FAFC]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-outfit text-sm font-semibold text-[#0F172A]">
                      {opt.label}
                    </div>
                    {active && (
                      <CheckCircle2 className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                    )}
                  </div>
                  <p className="font-figtree text-xs text-[#64748B] mt-1 leading-relaxed">
                    {opt.description}
                  </p>
                  <p className="font-figtree text-[11px] text-[#94A3B8] mt-2">
                    Fields: {opt.evaluation.join(" · ")}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="px-7 py-5 border-t border-slate-100 bg-[#FAFAFA] flex items-center justify-between">
          <p className="font-figtree text-xs text-[#94A3B8]">
            You can change this in Account → Therapy type.
          </p>
          <Button
            data-testid="onboarding-continue-btn"
            disabled={busy || !selected}
            onClick={handleSave}
            className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white font-figtree text-sm font-medium px-6"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
